import {
  keepPreviousData,
  QueryObserverResult,
  UseQueryResult,
  useQueryClient,
  useInfiniteQuery as useTanstackInfiniteQuery,
  useMutation as useTanstackMutation,
  useQuery as useTanstackQuery,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { api } from '~/api/HttpProvider'
import { ToastHandlerMessage } from '~/components/Toast/ToastConfig'
import { useToast } from '~/components/Toast/useToasts'

export type MutationToasts = {
  successToast?: ToastHandlerMessage
  errorToast?: ToastHandlerMessage
}

type UseQueryParams<T = unknown, S = T> = {
  url: string
  keys: (string | object)[]
  enabled?: boolean
  keepPreviousDataEnabled?: boolean
  refetchInterval?: number | false | ((query: { state: { data: T | undefined } }) => number | false)
  /**
   * Reshapes the response before the caller sees it, e.g. `sortByName`. Pass a module-level function, never an
   * inline arrow: tanstack only reruns `select` when its reference or the data changes.
   */
  select?: (data: T) => S
}

type UseInfiniteQueryParams<T> = UseQueryParams & {
  initialPageParam: number
  getNextPageParam: (lastPage: T, allPages: T[], lastPageParam: number) => number | undefined
  buildUrl: (url: string, page: number) => string
}

type UseMutationParams<TVariables = unknown> = {
  method: 'post' | 'put' | 'patch' | 'delete'
  /** Pass a function to build the url from the mutation variables, e.g. when the resource id is only known at call time. */
  url: string | ((variables: TVariables) => string)
  collectionKeys: (string | object)[]
  resourceId?: string
  isOptimistic?: boolean
  /** How long the mutation and its result stay in the cache once unused. Pass 0 when the response holds a secret. */
  gcTime?: number
} & MutationToasts

export const get = async <T>(url: string): Promise<T> => {
  const { data } = await api.get(url)
  return data
}

export const post = async <T>(url: string, body: any, signal: AbortSignal): Promise<T> => {
  const { data } = await api.post(url, body, { signal })
  return data
}

/**
 * Use this hook to fetch data (with GET method) when a component mounts or when the enabled flag is set to true.
 * @param url the url to fetch data from
 * @param keys the keys to use for the query
 * @param enabled the flag to enable the query
 * @param keepPreviousDataEnabled if true, the query will keep previous data while fetching new data
 * @param select reshapes the response, e.g. `sortByName`; a module-level function, so its reference is stable
 * @returns the query object
 */
export const useQuery = <T, S = T>({ url, keys, enabled = true, keepPreviousDataEnabled = false, refetchInterval, select }: UseQueryParams<T, S>) => {
  const toast = useToast()

  const query = useTanstackQuery<T, Error, S>({
    queryKey: keys,
    queryFn: () => get<T>(url),
    enabled,
    placeholderData: keepPreviousDataEnabled ? keepPreviousData : undefined,
    refetchInterval,
    select,
  })

  useEffect(() => {
    // Don't show toast for polling queries (refetchInterval)
    if (query.error && !refetchInterval) {
      toast.alert('An error occurred while trying to fetch data.')
    }
  }, [query.error, refetchInterval])

  return query
}

/**
 * Use this hook to fetch paginated data (with GET method) with infinite loading capability.
 * @param url the base url to fetch data from
 * @param keys the keys to use for the query
 * @param enabled the flag to enable the query
 * @param initialPageParam the initial page parameter
 * @param getNextPageParam function to get the next page parameter
 * @param buildUrl function to build the complete URL with page parameter
 * @returns the infinite query object
 */
export const useInfiniteQuery = <T>({ url, keys, enabled = true, initialPageParam, getNextPageParam, buildUrl }: UseInfiniteQueryParams<T>) => {
  const toast = useToast()

  const query = useTanstackInfiniteQuery({
    queryKey: keys,
    queryFn: async ({ pageParam: page }) => get<T>(buildUrl(url, page)),
    enabled,
    initialPageParam,
    getNextPageParam,
  })

  useEffect(() => {
    if (query.error) {
      toast.alert('An error occurred while trying to fetch data.')
    }
  }, [query.error, toast])

  return query
}

/**
 * Use this hook to fetch data on event (with GET method), e.g. if you need to fetch smth from the server on a button click.
 * @param url   the url to fetch data from
 * @param keys  the keys to use for the query
 * @returns the query object with a fetch method
 */
export const useLazyQuery = <T>({ url, keys }: Pick<UseQueryParams, 'url' | 'keys'>) => {
  // Use disabled query to prevent fetching data on mount
  const query = useQuery<T>({ url, keys, enabled: false })

  return {
    ...query,
    fetch: query.refetch, // Expose the refetch method as fetch to be more intuitive to use
  } as UseQueryResult<T, Error> & { fetch: () => Promise<QueryObserverResult<T, Error>> }
}

/**
 * Use this hook to perform changes on the server (POST, PUT, DELETE).
 * @param method the HTTP method to use 'post' | 'put' | 'delete'
 * @param url the url to fetch data from
 * @param collectionKeys the keys to use for the query
 * @param resourceId the id of the resource to update
 * @param isOptimistic if true, the mutation will be optimistic and will update the cache immediately
 * @param successToast the message to show on success
 * @param errorToast the message to show on error
 * @returns the mutation object
 */
export const useMutation = <TVariables = void, TData = unknown>({ method, url, collectionKeys, resourceId, isOptimistic = false, gcTime, successToast, errorToast }: UseMutationParams<TVariables>) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useTanstackMutation<TData, any, TVariables, { previousCollection: unknown; previousEntity: unknown }>({
    gcTime,
    mutationFn: async (body: TVariables | undefined) => {
      const resolvedUrl = typeof url === 'function' ? url(body as TVariables) : url
      try {
        const response = method === 'delete' ? await api[method](resolvedUrl) : await api[method](resolvedUrl, body)
        return response.data
      } catch (error) {
        if (error && typeof error === 'object' && 'response' in error) {
          throw (error as AxiosError).response?.data || error
        }
        throw error
      }
    },
    onMutate: async (data: TVariables) => {
      // Abort if the mutation is not optimistic
      if (!isOptimistic) {
        return { previousCollection: undefined, previousEntity: undefined }
      }

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: collectionKeys })
      await queryClient.cancelQueries({ queryKey: [...collectionKeys, resourceId] })

      // Snapshot the previous value
      const previousCollection = queryClient.getQueryData(collectionKeys)
      const previousEntity = queryClient.getQueryData([...collectionKeys, resourceId])

      // Optimistically update to the new value
      queryClient.setQueryData(collectionKeys, (old: TVariables[]) => [...(old || []).filter((e) => (e as any).id !== (data as any).id), data])
      queryClient.setQueryData([...collectionKeys, resourceId], data)

      // Return a context object with the snapshotted value
      return { previousCollection, previousEntity }
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_e, _variables, context) => {
      // Rollback to the previous value if the mutation is optimistic
      if (isOptimistic) {
        context && queryClient.setQueryData(collectionKeys, context.previousCollection)
        context && queryClient.setQueryData([...collectionKeys, resourceId], context.previousEntity)
      }

      if (errorToast) toast.alert(errorToast)
    },
    onSuccess: () => {
      if (successToast) toast.success(successToast)

      // Invalidate related queries on success if the mutation is not optimistic
      if (!isOptimistic) queryClient.invalidateQueries({ queryKey: collectionKeys })
    },
    onSettled: () => {
      // Invalidate related queries on settle if the mutation is optimistic
      if (isOptimistic) queryClient.invalidateQueries({ queryKey: collectionKeys })
    },
  })
}
